import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import Text from 'components/Text';
import { Row, Column } from 'components/Layout/Grid';
import { Button } from 'components/Button';
import RadioInput from 'components/RadioInput';
import Form from 'components/Form';
import Input from 'components/Input';
import toast from 'components/Toast/dispatch';

import IconSuccess from 'img/success-img.svg';

import SupplierService from 'services/supplier.service';

import { PhoneNumberOutput } from 'utils/PhoneNumber';
import SwitchComponent from 'components/SwitchComponent';
import UserService from 'services/user.service';
import { IUser } from 'models/User';

export const EditExternalUserSlider: React.FC = () => {
  const slider = useSelector((state: RootState) => state.slider);
  const sliderUser = slider?.state?.userProp;
  const user = useSelector((state: { user: IUser; }) => state.user);
  const _userService = new UserService();
  const [userStatus, setUserStatus] = useState<string>('');

  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState<any>({
    id: sliderUser.id,
    fullName: sliderUser.fullName,
    email: sliderUser?.email,
    roles: sliderUser?.roles,
    profileType: sliderUser?.profileType,
    supplierId: sliderUser?.supplierId,
    phoneNumber: PhoneNumberOutput(sliderUser?.phoneNumber || ''),
  });

  const dispatch = useDispatch();
  const _supplierService = new SupplierService();

  const textButtonSave = 'Editar';

  useEffect(() => {
    if (sliderUser) {
      setUserStatus(sliderUser.enabled ? 'enabled' : 'disabled');
      setUserData((prevData: any) => ({
        ...prevData,
        enabled: sliderUser.enabled,
        pending: sliderUser.pending,
      }));
    }
  }, [sliderUser]);

  function handleStatusChange(status: 'enabled' | 'disabled') {
    setUserStatus(status);
    setUserData((prevData: any) => ({
      ...prevData,
      enabled: status === 'enabled',
      pending: false
    }));
  }

  async function editExternalUser() {
    setLoading(true);
    setErrorMessage('');

    const dataUpdated = {
      id: userData.id,
      fullName: userData.fullName,
      enabled: userData.enabled,
      roles: userData.roles,
      phoneNumber: userData.phoneNumber
    };

    await _supplierService
      .updateExternalUser(dataUpdated)
      .then((result: any) => {
        if (result?.status === 200) {

          if (slider?.state?.currentPage > 0) {
            slider?.state?.setCurrentPage(0);
          } else {
            slider?.state?.getUsers();
          }

          dispatch({ type: 'CLOSE_TOAST' });

          dispatch({ type: 'HIDE_TITLE' });
          return setSuccess(true);
        }

        setErrorMessage(
          result.response.data.data || 'Ocorreu um erro durante a requisição.'
        );
        setLoading(false);
      });
  }

  function onCloseSlider() {
    slider?.state?.getUsers();
    dispatch({ type: 'CLOSE_SLIDER' });
    dispatch({ type: 'SHOW_TITLE' });
  }

  async function deleteUser() {
    setLoadingDelete(true);
    setErrorMessage('');

    try {
      await _userService.delete(sliderUser.id);

      dispatch({
        type: 'HIDE_TITLE',
      });

      setSuccess(true);
    } catch (err) {
      setErrorMessage(`Erro ao tentar excluir usuário ${sliderUser.fullName}.`);
      setSuccess(false);
    }

    setLoadingDelete(false);
    dispatch({ type: 'CLOSE_TOAST' });
  }

  async function confirmDeleteUser() {
    if (sliderUser?.id === user?.id) {
      toast.info('Atenção', 'Você não pode excluir o usuário logado.');
      return;
    }

    toast.yesNoProtocol(
      'Atenção',
      `Você deseja realmente excluir o usuário ${sliderUser.fullName}?`,
      () => deleteUser(),
      'Sim',
      'Não');
  }
  
  function setBtnRemoveUser() {
    return (
      <SwitchComponent roles={['administrator']} type={['supplier', 'employee']}>
        <Row className="mt-3 align-center justify-center">
          <Button
            onClick={() => confirmDeleteUser()}
            spinnerColorClass="color-danger"
            isRequesting={loadingDelete}
            className="outline border-danger bg-danger flex-sm-1 align-center justify-center d-flex px-3"
          >
            <Text className="color-danger font-bold">Remover usuário</Text>
          </Button>
        </Row>
      </SwitchComponent>
    );
  }

  return !success ? (
    <Form
      className=""
      id="add_new_user_form"
      onValidate={() => editExternalUser()}
    >
      <Column className="mt-3 flex-1">
        <Column>
          <Text className="color-blue-gray mb-2">Status</Text>
          <Row>
            <Row className="mr-2">
              <RadioInput
                id="radio_enabled"
                onClick={() => handleStatusChange('enabled')}
                checked={userStatus === 'enabled'}
              >
                <Text>Ativo</Text>
              </RadioInput>
            </Row>

            <Row>
              <RadioInput
                id="radio_disabled"
                onClick={() => handleStatusChange('disabled')}
                checked={userStatus === 'disabled'}
              >
                <Text>Inativo</Text>
              </RadioInput>
            </Row>
          </Row>
        </Column>
        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Nome*</Text>
          <Input
            className="w-sm-100"
            value={userData.fullName}
            onChange={(e) =>
              setUserData({ ...userData, fullName: e.target.value })
            }
            type="text"
            placeholder="Nome"
            required
          />
        </Column>
        <Column className="mt-4">
          <Text
            className="mr-2 mb-1 color-blue-gray"
            size={12}
            weight="bold"
          >
            E-mail*
          </Text>
          <Input
            disabled
            onChange={(e: any) =>
              setUserData({ ...userData, email: e.target.value.toLowerCase() })
            }
            className="w-100"
            type="email"
            value={userData.email}
            placeholder="E-mail"
          />
        </Column>
        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Telefone</Text>
          <Row>
            <Input
              className="w-sm-100"
              value={userData.phoneNumber}
              onChange={(e) =>
                setUserData({
                  ...userData,
                  phoneNumber: PhoneNumberOutput(e.target.value),
                })
              }
              type="text"
              placeholder="Telefone"
            />
          </Row>
        </Column>
        <Column className="mt-4">
          <Text className="color-blue-gray mb-2">Nível de acesso*</Text>
          <Row>
            <Row className="mr-2">
              <RadioInput
                id="radio_administrator"
                onClick={() =>
                  setUserData({ ...userData, roles: ['administrator'] })
                }
                checked={
                  userData.roles && userData.roles[0] === 'administrator'
                }
              >
                <Text className="color-blue-gray">Administrador</Text>
              </RadioInput>
            </Row>
            <Row>
              <RadioInput
                id="radio_collaborator"
                onClick={() =>
                  setUserData({ ...userData, roles: ['supplier'] })
                }
                checked={userData.roles && userData.roles[0] === 'supplier'}
              >
                <Text className="color-blue-gray">Colaborador</Text>
              </RadioInput>
            </Row>
          </Row>
        </Column>
        {errorMessage && (
          <Row className="mt-2">
            <Text size={12} className="animated fadeIn mt-1 color-danger">
              {errorMessage}
            </Text>
          </Row>
        )}
        <Row className="mt-3 align-center justify-center">
          <Button
            form="add_new_user_form"
            spinnerColor="#0E4C54"
            isRequesting={loading}
            disabled={loading}
            className="outline bg-primary flex-sm-1 align-center justify-center d-flex pr-5 pl-5"
          >
            <Text className="color-primary font-bold">{textButtonSave}</Text>
          </Button>
        </Row>
        {setBtnRemoveUser()}
      </Column>
    </Form>
  ) : (
    <Column className="w-100 h-100 align-center justify-center">
      <img src={IconSuccess} alt="" />
      <Text className="mt-2 color-blue-gray font-bold">
        Usuário editado com sucesso
      </Text>
      <Button onClick={onCloseSlider} className="outline mt-4" width="auto">
        <Text className="color-primary font-bold px-3">Fechar</Text>
      </Button>
    </Column>
  );
};